<ng-container *ngIf="open">
  <div class="modal" (click)="close()">
    <tm1-card class="modal-content historical-modal-content" (click)="$event.stopPropagation()">
      <span class="close" (click)="close()">&times;</span>
      <tm1-typography type="data-l">Get Historical Data</tm1-typography>
      <p class="historical-subtitle">
        Select venues and a date range, then extract a summary for each venue across that range.
      </p>

      <div class="historical-controls">
        <!-- ===================== Venue picker ===================== -->
        <div class="historical-venues">
          <div class="historical-section-label">
            <strong>Venues</strong>
            <span class="historical-selected-count" *ngIf="selectedCount">{{ selectedCount }} selected</span>
          </div>

          <ng-container *ngIf="venuesLoading">
            <div class="historical-status">
              <tm1-progress-ring value="" min="0" max="1" size="small"></tm1-progress-ring>
              <span>Loading venues…</span>
            </div>
          </ng-container>

          <ng-container *ngIf="venuesError">
            <div class="historical-status">We couldn't load the venue list. Please reopen and try again.</div>
          </ng-container>

          <ng-container *ngIf="!venuesLoading && !venuesError">
            <input
              type="text"
              class="form-control historical-search"
              placeholder="Search venues…"
              [(ngModel)]="searchTerm"
              autocomplete="off"
            />

            <div class="historical-picker-actions">
              <button type="button" class="link-btn" (click)="selectAllVisible()">Select all{{ searchTerm ? ' shown' : '' }}</button>
              <button type="button" class="link-btn" (click)="clearSelection()" [disabled]="!selectedCount">Clear</button>
            </div>

            <div class="historical-tree">
              <ng-container *ngFor="let continent of tree; trackBy: trackByContinent">
                <div class="venue-group" *ngIf="continentHasMatches(continent)">
                  <div class="venue-group__header">
                    <input
                      type="checkbox"
                      class="venue-check"
                      [checked]="isContinentSelected(continent.continent)"
                      [indeterminate]="isContinentIndeterminate(continent.continent)"
                      (change)="toggleContinent(continent.continent)"
                      (click)="$event.stopPropagation()"
                    />
                    <button
                      type="button"
                      class="venue-group__toggle"
                      (click)="toggleGroup('continent:' + continent.continent)"
                    >
                      <span
                        class="venue-group__chevron"
                        [class.expanded]="isExpanded('continent:' + continent.continent) || searchTerm"
                        aria-hidden="true"
                      ></span>
                      <span class="venue-group__title">{{ continent.continent }}</span>
                    </button>
                  </div>

                  <div
                    class="venue-group__body"
                    *ngIf="isExpanded('continent:' + continent.continent) || searchTerm"
                  >
                    <ng-container *ngFor="let city of continent.cities; trackBy: trackByCity">
                      <div class="venue-subgroup" *ngIf="cityHasMatches(city.venues)">
                        <div class="venue-subgroup__header">
                          <input
                            type="checkbox"
                            class="venue-check"
                            [checked]="isCitySelected(continent.continent, city.city)"
                            [indeterminate]="isCityIndeterminate(continent.continent, city.city)"
                            (change)="toggleCity(continent.continent, city.city)"
                            (click)="$event.stopPropagation()"
                          />
                          <button
                            type="button"
                            class="venue-group__toggle"
                            (click)="toggleGroup(cityId(continent.continent, city.city))"
                          >
                            <span
                              class="venue-group__chevron"
                              [class.expanded]="isExpanded(cityId(continent.continent, city.city)) || searchTerm"
                              aria-hidden="true"
                            ></span>
                            <span class="venue-subgroup__title">{{ city.city }}</span>
                          </button>
                        </div>

                        <div
                          class="venue-subgroup__body"
                          *ngIf="isExpanded(cityId(continent.continent, city.city)) || searchTerm"
                        >
                          <label
                            class="venue-option"
                            *ngFor="let venue of filterVenues(city.venues); trackBy: trackByVenue"
                          >
                            <input
                              type="checkbox"
                              class="venue-check"
                              [checked]="isVenueSelected(venue.key)"
                              (change)="toggleVenue(venue.key)"
                            />
                            <span class="venue-option__name">{{ venue.value.name }}</span>
                          </label>
                        </div>
                      </div>
                    </ng-container>
                  </div>
                </div>
              </ng-container>

              <div class="historical-empty" *ngIf="!venues.length">No venues available.</div>
            </div>
          </ng-container>
        </div>

        <!-- ===================== Date range + actions ===================== -->
        <div class="historical-dates">
          <div class="historical-section-label"><strong>Date range</strong></div>

          <label class="historical-date-field">
            <span>From</span>
            <input type="date" class="form-control" [(ngModel)]="startDate" [max]="today" />
          </label>
          <label class="historical-date-field">
            <span>To</span>
            <input type="date" class="form-control" [(ngModel)]="endDate" [max]="today" />
          </label>

          <p class="historical-range-warning" *ngIf="rangeInvalid">
            The start date must be on or before the end date.
          </p>

          <button
            type="button"
            class="extract-btn"
            [disabled]="!canExtract"
            (click)="extract()"
          >
            Extract
          </button>

          <!-- Progress -->
          <ng-container *ngIf="extracting">
            <div class="historical-progress">
              <div class="historical-progress-bar">
                <div class="historical-progress-fill" [style.width.%]="progressPercent"></div>
              </div>
              <span class="historical-progress-text">
                {{ completedCount }} / {{ totalCount }} venues ({{ progressPercent }}%)
              </span>
            </div>
          </ng-container>

          <p class="historical-error" *ngIf="extractError">{{ extractError }}</p>
          <p class="historical-warning" *ngIf="!extracting && failedVenues.length">
            {{ failedVenues.length }} venue(s) couldn't be loaded: {{ failedVenues.join(', ') }}
          </p>
        </div>
      </div>

      <!-- ===================== Results table ===================== -->
      <ng-container *ngIf="hasResults && rows.length">
        <div class="historical-results-header">
          <tm1-typography type="data-m">Results</tm1-typography>
          <div class="historical-export-actions">
            <label class="historical-hide-empty">
              <input type="checkbox" [checked]="hideEmpty" (change)="toggleHideEmpty()" />
              <span>Hide venues with 0 scans<ng-container *ngIf="hideEmpty && hiddenCount"> ({{ hiddenCount }})</ng-container></span>
            </label>
            <button type="button" class="export-btn" (click)="copyToClipboard()" (mouseleave)="resetCopyState()">
              {{ copyState === 'copied' ? 'Copied!' : 'Copy' }}
            </button>
            <button type="button" class="export-btn export-btn--primary" (click)="downloadExcel()">
              Download Excel
            </button>
          </div>
        </div>

        <div class="extended-summary-table-wrapper">
          <table class="extended-summary-table">
            <thead>
              <tr>
                <th class="sortable" (click)="toggleSort('name')">Venue <span class="sort-indicator">{{ sortIndicator('name') }}</span></th>
                <th class="sortable" (click)="toggleSort('entries')">Entries <span class="sort-indicator">{{ sortIndicator('entries') }}</span></th>
                <th class="sortable" (click)="toggleSort('exits')">Exits <span class="sort-indicator">{{ sortIndicator('exits') }}</span></th>
                <th class="sortable" (click)="toggleSort('internal')">Internal <span class="sort-indicator">{{ sortIndicator('internal') }}</span></th>
                <th class="sortable" (click)="toggleSort('reentries')">Reentries <span class="sort-indicator">{{ sortIndicator('reentries') }}</span></th>
                <th class="sortable" (click)="toggleSort('inVenue')">Inside <span class="sort-indicator">{{ sortIndicator('inVenue') }}</span></th>
                <ng-container *ngIf="rejectColumns.length">
                  <th class="reject-col reject-pct-col sortable" (click)="toggleSort('__rejectPct')">Reject % <span class="sort-indicator">{{ sortIndicator('__rejectPct') }}</span></th>
                  <th class="reject-col reject-total-col sortable"
                    [class.expanded]="rejectsExpanded"
                    title="Click to {{ rejectsExpanded ? 'hide' : 'show' }} reject breakdown">
                    <span class="reject-total-label" (click)="toggleSort('__rejectTotal'); $event.stopPropagation()">
                      Rejects
                      <span class="sort-indicator">{{ sortIndicator('__rejectTotal') }}</span>
                    </span>
                    <span class="reject-toggle-arrow" (click)="toggleRejectsExpanded(); $event.stopPropagation()">
                      {{ rejectsExpanded ? '▾' : '▸' }}
                    </span>
                  </th>
                  <ng-container *ngIf="rejectsExpanded">
                    <th class="reject-col sortable" *ngFor="let code of rejectColumns"
                      (click)="toggleSort('__reject:' + code)">
                      {{ formatErrorCode(code) }} <span class="sort-indicator">{{ sortIndicator('__reject:' + code) }}</span>
                    </th>
                  </ng-container>
                </ng-container>
              </tr>
            </thead>
            <tbody>
              <tr class="totals-row">
                <td class="primary-cell">All Venues Total</td>
                <td>{{ totalsRow.entries }}</td>
                <td>{{ totalsRow.exits }}</td>
                <td>{{ totalsRow.internal }}</td>
                <td>{{ totalsRow.reentries }}</td>
                <td>{{ totalsRow.inVenue }}</td>
                <ng-container *ngIf="rejectColumns.length">
                  <td class="reject-col reject-pct-col" [class.is-zero]="!rejectTotal(totalsRow.rejects)">
                    {{ rejectPercent(totalsRow) | number: '1.0-2' }}%
                  </td>
                  <td class="reject-col reject-total-col" [class.is-zero]="!rejectTotal(totalsRow.rejects)">
                    {{ rejectTotal(totalsRow.rejects) }}
                  </td>
                  <ng-container *ngIf="rejectsExpanded">
                    <td class="reject-col" *ngFor="let code of rejectColumns"
                      [class.is-zero]="!rejectCount(totalsRow.rejects, code)">
                      {{ rejectCount(totalsRow.rejects, code) }}
                    </td>
                  </ng-container>
                </ng-container>
              </tr>
              <ng-container *ngFor="let row of sortedRows">
                <tr class="venue-row" [class.expandable]="row.events.length" (click)="row.events.length && toggleRowExpanded(row.key)">
                  <td class="primary-cell">
                    <span
                      class="event-toggle-arrow"
                      *ngIf="row.events.length"
                      [class.expanded]="isRowExpanded(row.key)"
                      aria-hidden="true"
                    >▸</span>
                    {{ row.name }}
                    <span class="event-count" *ngIf="row.events.length">{{ row.events.length }}</span>
                  </td>
                  <td>{{ row.entries }}</td>
                  <td>{{ row.exits }}</td>
                  <td>{{ row.internal }}</td>
                  <td>{{ row.reentries }}</td>
                  <td>{{ row.inVenue }}</td>
                  <ng-container *ngIf="rejectColumns.length">
                    <td class="reject-col reject-pct-col" [class.is-zero]="!rejectTotal(row.rejects)">
                      {{ rejectPercent(row) | number: '1.0-2' }}%
                    </td>
                    <td class="reject-col reject-total-col" [class.is-zero]="!rejectTotal(row.rejects)">
                      {{ rejectTotal(row.rejects) }}
                    </td>
                    <ng-container *ngIf="rejectsExpanded">
                      <td class="reject-col" *ngFor="let code of rejectColumns"
                        [class.is-zero]="!rejectCount(row.rejects, code)">
                        {{ rejectCount(row.rejects, code) }}
                      </td>
                    </ng-container>
                  </ng-container>
                </tr>

                <!-- Per-event breakdown, revealed when the venue row is expanded. -->
                <tr class="event-row" *ngFor="let event of row.events" [hidden]="!isRowExpanded(row.key)">
                  <td class="primary-cell event-cell" [title]="event.key">
                    <span class="event-date" *ngIf="event.date">{{ event.date }}</span>
                    {{ event.name }}
                  </td>
                  <td>{{ event.entries }}</td>
                  <td>{{ event.exits }}</td>
                  <td>{{ event.internal }}</td>
                  <td>{{ event.reentries }}</td>
                  <td>{{ event.inVenue }}</td>
                  <ng-container *ngIf="rejectColumns.length">
                    <td class="reject-col reject-pct-col" [class.is-zero]="!rejectTotal(event.rejects)">
                      {{ rejectPercent(event) | number: '1.0-2' }}%
                    </td>
                    <td class="reject-col reject-total-col" [class.is-zero]="!rejectTotal(event.rejects)">
                      {{ rejectTotal(event.rejects) }}
                    </td>
                    <ng-container *ngIf="rejectsExpanded">
                      <td class="reject-col" *ngFor="let code of rejectColumns"
                        [class.is-zero]="!rejectCount(event.rejects, code)">
                        {{ rejectCount(event.rejects, code) }}
                      </td>
                    </ng-container>
                  </ng-container>
                </tr>
              </ng-container>
            </tbody>
          </table>
        </div>
      </ng-container>
    </tm1-card>
  </div>
</ng-container>
